import { useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import useActionsFirstColumnOrder from '../../../hooks/useActionsFirstColumnOrder';
import { processTableFilterOptions } from '../../../utils/table-helpers';
import { HEADERS, PROPERTY_PATH_MAP } from './constants';
import { getClaimTypeLabel } from './constants';
import messages from './messages';
import parentMessages from '../messages';
import ApprovedAmountCell from '../ApprovedAmountCell';
import { VARIANCE_CELL_SX } from '../constants';
import {
  canDelete,
  getFirstMatchingReason,
  canEditApprovedAmount,
} from '../utils';

function useBuildFilterOptions({ results }) {
  return useMemo(() => {
    const options = {
      status: [],
      claimType: [],
      propertyTransactionCodeId: [],
    };

    if (results) {
      results.forEach((result) => {
        if (result.statusDisplay) options.status.push(result.statusDisplay);
        if (result.claimTypeDisplay)
          options.claimType.push(result.claimTypeDisplay);
        else if (result.claimType)
          options.claimType.push(getClaimTypeLabel(result.claimType));
        if (result.propertyTransactionCodeDisplay)
          options.propertyTransactionCodeId.push(
            result.propertyTransactionCodeDisplay,
          );
      });
    }

    processTableFilterOptions(options, undefined, {
      showCounts: false,
    });
    return options;
  }, [results]);
}

function useHeaders({ results }) {
  const filterOptions = useBuildFilterOptions({ results });

  return useMemo(() => {
    return HEADERS.map((header) => ({
      ...header,
      filterOptions: header.filterOptions ?? filterOptions[header.id],
    }));
  }, [filterOptions]);
}

function useRows({
  isReadOnly,
  specialClaims,
  onDelete,
  voucherSubmissionStatus,
  hasVariance,
  onSaveApprovedAmount,
  canManage,
}) {
  return useMemo(() => {
    return specialClaims.map((row) => {
      const isPermissionDisabled = !canManage;
      const isDeleteDisabled =
        isPermissionDisabled ||
        isReadOnly ||
        !canDelete(row.status, voucherSubmissionStatus);

      return {
        id: row.id,
        claimType: {
          value: row.claimTypeDisplay ?? getClaimTypeLabel(row.claimType),
        },
        hudClaimId: {
          value: row.hudClaimId,
        },
        requestedAmount: {
          variant: 'currency',
          numberFormatOptions: { minimumFractionDigits: 2 },
          value: row.requestedAmount,
        },
        approvedAmount: canEditApprovedAmount(
          hasVariance,
          isReadOnly,
          canManage,
        )
          ? {
              sx: VARIANCE_CELL_SX,
              value: (
                <ApprovedAmountCell
                  key={row.id}
                  initialValue={row.approvedAmount}
                  onSave={(amount) => onSaveApprovedAmount?.(row, amount)}
                />
              ),
            }
          : {
              variant: 'currency',
              numberFormatOptions: { minimumFractionDigits: 2 },
              value: row.approvedAmount,
            },
        propertyTransactionCodeId: {
          value:
            row.propertyTransactionCodeDisplay ?? row.propertyTransactionCodeId,
        },
        actions: {
          variant: 'menu',
          name: 'actions',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'delete',
              text: <FormattedMessage {...messages.delete} />,
              disabled: isDeleteDisabled,
              tooltip: getFirstMatchingReason([
                {
                  condition: isPermissionDisabled,
                  message: (
                    <FormattedMessage {...parentMessages.noPermissionTooltip} />
                  ),
                },
              ]),
              onClick: !isDeleteDisabled ? () => onDelete(row) : null,
            },
          ],
        },
      };
    });
  }, [
    specialClaims,
    voucherSubmissionStatus,
    isReadOnly,
    onDelete,
    hasVariance,
    onSaveApprovedAmount,
    canManage,
  ]);
}

export function useSpecialClaimsTable({
  isReadOnly,
  specialClaims = [],
  onDelete,
  voucherSubmissionStatus,
  hasVariance = false,
  onSaveApprovedAmount,
  canManage,
}) {
  const name = useUniqueTableName('special-claims-table');
  const baseTableProps = useBaseTableProps();

  const headers = useHeaders({ results: specialClaims });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'hudClaimId',
    initialOrder: 'DESC',
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: specialClaims,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const handleDelete = useCallback(
    (claim) => {
      if (onDelete) onDelete(claim);
    },
    [onDelete],
  );

  const rows = useRows({
    isReadOnly,
    specialClaims: sortedAndFilteredResults,
    onDelete: handleDelete,
    voucherSubmissionStatus,
    hasVariance,
    onSaveApprovedAmount,
    canManage,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  return {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    filterState,
    filterTypeState,
    dateState,
    searchState,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    isLoading: false,
    order,
    orderBy,
    rows,
    count: rows.length,
    totalCount: specialClaims.length,
  };
}
